@file:Suppress("MagicNumber", "LongMethod", "LargeClass", "TooManyFunctions")

package com.nonopn.skydome.common.app.pages.pdf

import com.nonopn.skydome.common.app.utils.fmt
import kotlin.math.PI
import kotlin.math.abs
import kotlin.math.cos
import kotlin.math.max
import kotlin.math.roundToInt
import kotlin.math.sin

/**
 * Generates the self-contained 12-page Keraunic Report HTML.
 * Matches the validated template preview_keraunic_v4ak.html.
 *
 * Data models for dynamic injection:
 */
data class KeraunicZoneStats(
    val totalCount: Int,
    val negCount: Int = 0,
    val posCount: Int = 0,
    val negMaxKa: Double = 0.0,
    val negAvgKa: Double = 0.0,
    val negMedianKa: Double = 0.0,
    val posMaxKa: Double = 0.0,
    val posAvgKa: Double = 0.0,
    val posMedianKa: Double = 0.0,
)

data class KeraunicTopImpact(
    val externalId: String,
    val dateLabel: String,
    val distKm: Double,
    val amplitudeKa: Double,
    val isPositive: Boolean,
    val percent: Double,
    val sensorCount: Int = 0,
    // Time of the impact (UTC seconds) when the server provides it
    val atSecondsUtc: Long? = null,
    // Same time in the device time zone, shown as date, hour and UTC offset
    val localTime: KeraunicLocalTime? = null,
)

// Nsg of a nearby reference city, for the regional comparison
data class KeraunicNsgRef(
    val label: String,
    val value: Double,
)

data class KeraunicMonthlyData(
    val month: String, // "Jan", "Fév", etc.
    val negCount: Int = 0,
    val posCount: Int = 0,
    val negAvgKa: Double = 0.0,
    val posAvgKa: Double = 0.0,
)

data class KeraunicHourlyData(
    val hour: Int, // 0-23
    val negCount: Int = 0,
    val posCount: Int = 0,
    val negAvgKa: Double = 0.0,
    val posAvgKa: Double = 0.0,
)

object PdfKeraunicBuilder {

    private const val LOGO_B64 = "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"

    // ─── Public API ─────────────────────────────────────────────────────────

    /** Renders the report computed by [KeraunicReportCalculator] (same data as the screen). */
    fun build(
        lang: PdfOutputLang,
        projectName: String,
        data: KeraunicReportData,
        generatedDate: String,
        questionnaire: PdfQuestionnaireData? = null,
        customTitle: String = "",
        observations: String = "",
    ): String = build(
        lang = lang,
        projectName = projectName,
        projectLat = data.latitude,
        projectLon = data.longitude,
        periodLabel = data.periodLabel,
        nsgValue = data.nsg,
        zone1Stats = data.zone1,
        zone2Stats = data.zone2,
        topImpacts = data.topImpacts,
        monthly1Km = data.monthly1Km,
        monthly2Km = data.monthly2Km,
        hourly1Km = data.hourly,
        questionnaire = questionnaire,
        generatedDate = generatedDate,
        mapImpacts = data.mapImpacts,
        nsgRefs = data.nsgRefs,
        fallbackImpacts = data.fallbackImpacts,
        customTitle = customTitle,
        observations = observations,
        highlights = data.highlights,
    )

    @Suppress("LongParameterList")
    fun build(
        lang: PdfOutputLang,
        projectName: String,
        projectLat: Double,
        projectLon: Double,
        periodLabel: String,
        nsgValue: Double,
        zone1Stats: KeraunicZoneStats,
        zone2Stats: KeraunicZoneStats,
        topImpacts: List<KeraunicTopImpact> = emptyList(),
        monthly1Km: List<KeraunicMonthlyData> = emptyList(),
        monthly2Km: List<KeraunicMonthlyData> = emptyList(),
        hourly1Km: List<KeraunicHourlyData> = emptyList(),
        questionnaire: PdfQuestionnaireData? = null,
        generatedDate: String = "",
        mapImpacts: List<SvgMapBuilder.ImpactDot> = emptyList(),
        nsgRefs: List<KeraunicNsgRef> = emptyList(),
        fallbackImpacts: List<KeraunicTopImpact> = emptyList(),
        customTitle: String = "",
        observations: String = "",
        highlights: KeraunicHighlights? = null,
    ): String {
        val text = KeraunicReportText.forLanguage(lang)
        val localizedPeriod = text.period(periodLabel)
        val localizedGeneratedDate = text.date(generatedDate)
        val coordStr = text.coordinates(projectLat, projectLon)
        val totalImpacts = zone1Stats.totalCount + zone2Stats.totalCount
        val maxKa = KeraunicReportLogic.overallMaxKa(zone1Stats, zone2Stats)
        val mapSvg = SvgMapBuilder.impactMap(
            projectLat, projectLon, mapImpacts,
            zone1Stats.totalCount, zone2Stats.totalCount,
            lang = lang
        )
        val totalPages = 12

        return buildString {
            append(htmlHead(lang, text))
            append(pageCover(projectName, customTitle, coordStr, localizedPeriod, nsgValue, totalImpacts, zone1Stats.totalCount, maxKa, highlights, localizedGeneratedDate, totalPages, text))
            append(
                pageOverview(
                    projectName, localizedPeriod, topImpacts, fallbackImpacts,
                    nsgValue, nsgRefs, coordStr, localizedGeneratedDate, totalPages, text
                )
            )
            append(pageImpactMap(projectName, localizedPeriod, mapSvg, zone1Stats.totalCount, zone2Stats.totalCount, localizedGeneratedDate, 3, totalPages, text))
            append(pageZone(projectName, localizedPeriod, "0\u20131", "direct", zone1Stats, topImpacts, localizedGeneratedDate, 4, totalPages, text))
            append(pageZone(projectName, localizedPeriod, "1\u20132", "indirect", zone2Stats, emptyList(), localizedGeneratedDate, 5, totalPages, text))
            append(pageEffects(projectName, localizedPeriod, zone1Stats, zone2Stats, maxKa, nsgValue, localizedGeneratedDate, totalPages, text))
            append(pageMonthlyActivity(projectName, localizedPeriod, monthly1Km, monthly2Km, localizedGeneratedDate, totalPages, text))
            append(pageMonthlyIntensity(projectName, localizedPeriod, monthly1Km, monthly2Km, localizedGeneratedDate, totalPages, text))
            append(pageHourlyDistribution(projectName, localizedPeriod, hourly1Km, localizedGeneratedDate, totalPages, text))
            append(pageHourlyIntensity(projectName, localizedPeriod, hourly1Km, localizedGeneratedDate, totalPages, text))
            append(pageRecommendations(projectName, localizedPeriod, zone1Stats, zone2Stats, maxKa, nsgValue, questionnaire, localizedGeneratedDate, totalPages, text))
            append(pageMethodology(projectName, localizedPeriod, observations, localizedGeneratedDate, totalPages, text))
            append("\n</body>\n</html>")
        }
    }

    // ─── HTML Head + CSS ────────────────────────────────────────────────────

    private fun htmlHead(lang: PdfOutputLang, text: KeraunicReportText) = """
<!DOCTYPE html>
<html lang="${lang.code}">
<head>
<meta charset="UTF-8">
<title>${text.reportTitle}</title>
<style>
  @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800;900&display=swap');
  *{margin:0;padding:0;box-sizing:border-box}
  body{font-family:'Inter',-apple-system,sans-serif;background:#F5F7FA;color:#1A2B4A;font-size:15px}
  .page{width:210mm;min-height:297mm;margin:0 auto 24px;background:#fff;padding:0 0 60px;position:relative;overflow:hidden}
  .header-bar{display:flex;align-items:center;gap:14px;padding:16px 32px 12px;border-bottom:3px solid #2B7FE0}
  .header-bar img{height:52px;max-height:52px}
  .header-meta{font-size:14px;color:#4A5A7A;line-height:1.5}
  .header-meta strong{color:#1A2B4A;font-size:15px}
  .header-site{font-size:15px;font-weight:700;color:#1A2B4A}
  .header-period{font-size:13px;color:#6B7A99}
  .body{padding:18px 32px}
  .section-title{font-size:12px;font-weight:600;color:#2B7FE0;text-transform:uppercase;letter-spacing:.08em;margin:16px 0 10px}
  .section-title:first-child{margin-top:0}
  .kpi-grid{display:grid;grid-template-columns:repeat(4,1fr);gap:10px;margin:14px 0 18px}
  .kpi-card{background:#F5F7FA;border:1px solid #E0E6F0;border-radius:10px;padding:14px;text-align:center}
  .kpi-value{font-size:28px;font-weight:800;color:#1A2B4A;line-height:1.1}
  .kpi-value.blue{color:#2B7FE0}
  .kpi-value.red{color:#E63B2E}
  .kpi-label{font-size:12px;color:#6B7A99;text-transform:uppercase;letter-spacing:.06em;margin-top:6px}
  .top-impact{display:flex;gap:10px;padding:10px 14px;background:#F9FAFB;border:1px solid #E0E6F0;border-radius:8px;margin-bottom:8px;align-items:center}
  .top-rank{font-size:20px;font-weight:800;color:#8A9AB5;min-width:28px;text-align:center}
  .top-info{flex:1}
  .top-ka{font-size:18px;font-weight:700;white-space:nowrap}
  .top-ka.blue{color:#2B7FE0} .top-ka.red{color:#E63B2E}
  .top-meta{font-size:12px;color:#6B7A99;margin-top:2px}
  .top-pct{font-size:16px;font-weight:700;color:#F5A800;text-align:right;min-width:60px}
  .zone-card{border-radius:12px;padding:18px 22px;margin-bottom:14px}
  .zone-card.direct{background:#FFF5F5;border:2px solid #E63B2E}
  .zone-card.indirect{background:#FFFBF0;border:2px solid #F5A800}
  .zone-badge{display:inline-block;padding:3px 12px;border-radius:4px;font-size:12px;font-weight:700;text-transform:uppercase;letter-spacing:.06em;color:#fff;margin-bottom:8px}
  .zone-badge.red{background:#E63B2E} .zone-badge.amber{background:#F5A800}
  .zone-title{font-size:20px;font-weight:800;margin-bottom:4px}
  .zone-sub{font-size:14px;color:#4A5568;line-height:1.5}
  .stats-grid{display:grid;grid-template-columns:1fr 1fr;gap:14px;margin-top:14px}
  .stat-group h4{font-size:14px;font-weight:700;margin-bottom:8px}
  .stat-row{display:flex;justify-content:space-between;padding:5px 0;border-bottom:1px solid #EEF2F8}
  .stat-lbl{font-size:14px;color:#4A5568}
  .stat-val{font-size:15px;font-weight:700}
  .stat-val.blue{color:#2B7FE0} .stat-val.red{color:#E63B2E}
  .polar-bar-wrap{width:360px;margin:0 auto}
  .polar-bar{display:flex;height:22px;border-radius:11px;overflow:hidden;margin-bottom:6px}
  .polar-neg{background:#2B7FE0;height:100%} .polar-pos{background:#E63B2E;height:100%}
  .polar-labels{display:flex;justify-content:space-between;font-size:14px;font-weight:600}
  .chart-box{background:#F9FAFB;border:1px solid #E0E6F0;border-radius:10px;margin-bottom:8px}
  .chart-legend{display:flex;gap:16px;font-size:12px;color:#4A5A7A;margin-bottom:4px}
  .legend-dot{display:inline-block;width:10px;height:10px;border-radius:2px;vertical-align:middle;margin-right:4px}
  .interp{border-radius:8px;padding:10px 14px;font-size:14px;line-height:1.6;margin:8px 0}
  .interp.blue{background:#EEF4FF;border-left:4px solid #2B7FE0;color:#1A4A8A}
  .interp.red{background:#FFF3F3;border-left:4px solid #E63B2E;color:#7B1F1A}
  .interp.amber{background:#FFF8E8;border-left:4px solid #F5A800;color:#6B4A00}
  .cover-risk{display:flex;align-items:center;gap:20px;padding:16px 24px;margin-bottom:14px}
  .cover-risk-label{font-size:24px;font-weight:900;color:#E63B2E;text-transform:uppercase}
  .cover-risk-sub{font-size:14px;color:#4A5568;line-height:1.5;margin-top:4px}
  .reco-card{border-radius:10px;padding:14px 18px;margin-bottom:10px;display:flex;gap:14px;align-items:flex-start}
  .reco-card.critical{background:#FFF3F3;border:1.5px solid #E63B2E}
  .reco-card.important{background:#FFF8E8;border:1.5px solid #F5A800}
  .reco-card.advisory{background:#EEF4FF;border:1.5px solid #2B7FE0}
  .reco-card.sky{background:#F0FBF4;border:1.5px solid #2E7D32}
  .reco-prio{font-size:10px;font-weight:700;text-transform:uppercase;padding:4px 10px;border-radius:4px;white-space:nowrap;margin-top:2px}
  .reco-prio.p1{background:#E63B2E;color:#fff} .reco-prio.p2{background:#F5A800;color:#fff}
  .reco-prio.p3{background:#2B7FE0;color:#fff} .reco-prio.px{background:#2E7D32;color:#fff}
  .reco-title{font-size:15px;font-weight:700;color:#1A2B4A;margin-bottom:4px}
  .reco-desc{font-size:13px;color:#2D3748;line-height:1.5}
  .badge-norm{display:inline-block;padding:2px 8px;border:1px solid #C8D6E5;border-radius:4px;font-size:11px;font-weight:600;color:#4A5A7A;background:#F5F7FA;margin-right:4px}
  .footer{position:absolute;bottom:20px;left:32px;right:32px;border-top:1px solid #D0D9E8;padding-top:8px;display:flex;justify-content:space-between;align-items:center;font-size:11px;color:#8A97B0}
  .norms{display:flex;align-items:center;gap:6px}
  @media print{body{background:#fff}.page{margin:0;box-shadow:none}}
</style>
</head>
<body>
"""

    // ─── Shared helpers ─────────────────────────────────────────────────────

    private fun riskLabel(text: KeraunicReportText, level: KeraunicRiskLevel): String = when (level) {
        KeraunicRiskLevel.HIGH -> text.recommendations.riskHigh
        KeraunicRiskLevel.MODERATE -> text.recommendations.riskModerate
        KeraunicRiskLevel.LOW -> text.recommendations.riskLow
    }

    /** Free text typed by the user (title, observations), rendered as text and never as HTML. */
    internal fun escapeHtml(value: String): String = value
        .replace("&", "&amp;")
        .replace("<", "&lt;")
        .replace(">", "&gt;")
        .replace("\"", "&quot;")

    private fun fill(template: String, vararg values: String): String =
        KeraunicReportText.forLanguage(PdfOutputLang.FR).fill(template, *values)

    private fun headerBar(text: KeraunicReportText, subtitle: String, siteName: String, period: String) = """
  <div class="header-bar">
    <img src="data:image/png;base64,$LOGO_B64" alt="Strike Radar">
    <div class="header-meta">
      <strong>$subtitle</strong><br/>
      $siteName &nbsp;|&nbsp; $period<br/>
    </div>
  </div>"""

    private fun footer(text: KeraunicReportText, siteName: String, genDate: String, pageNum: Int, totalPages: Int) = """
  <div class="footer">
    <span>Strike Radar &nbsp;·&nbsp; $siteName &nbsp;·&nbsp; ${text.editedOn} $genDate</span>
    <div class="norms">
      <span class="badge-norm">IEC 62305-2:2024</span>
      <span class="badge-norm">IEC 62858:2019</span>
      <span style="font-size:15px;color:#8A9AB5;font-weight:500">$pageNum / $totalPages</span>
    </div>
  </div>"""

    private fun polarityBar(text: KeraunicReportText, negCount: Int, posCount: Int, total: Int): String {
        if (total == 0) return ""
        val (negPct, posPct) = KeraunicReportLogic.polarityPercents(negCount, total)
        return """
    <div style="text-align:center;margin:14px 0 18px;">
      <div style="font-size:15px;color:#6B7A99;margin-bottom:10px;font-weight:600;text-transform:uppercase;letter-spacing:.05em;">${text.zone.polarityDistribution}</div>
      <div class="polar-bar-wrap" style="width:360px;margin:0 auto;">
        <div class="polar-bar">
          <div class="polar-neg" style="width:$negPct%"></div>
          <div class="polar-pos" style="width:$posPct%"></div>
        </div>
        <div class="polar-labels" style="font-size:15px;font-weight:600;">
          <span style="color:#2B7FE0">■ CG− $negCount ($negPct%)</span>
          <span style="color:#E63B2E">CG+ $posCount ($posPct%) ■</span>
        </div>
      </div>
    </div>"""
    }

    private fun statsGrid(text: KeraunicReportText, stats: KeraunicZoneStats): String {
        return """
    <div class="stats-grid">
      <div class="stat-group">
        <h4 style="color:#2B7FE0">⚡ ${text.zone.negativeLightning}</h4>
        <div class="stat-row"><span class="stat-lbl">${text.zone.impactCount}</span><span class="stat-val blue">${stats.negCount}</span></div>
        <div class="stat-row"><span class="stat-lbl">${text.zone.maximumIntensity}</span><span class="stat-val blue">${stats.negMaxKa.fmt(0)} kA</span></div>
        <div class="stat-row"><span class="stat-lbl">${text.zone.averageIntensity}</span><span class="stat-val blue">${stats.negAvgKa.fmt(0)} kA</span></div>
        <div class="stat-row"><span class="stat-lbl">${text.zone.median}</span><span class="stat-val blue">${stats.negMedianKa.fmt(0)} kA</span></div>
      </div>
      <div class="stat-group">
        <h4 style="color:#E63B2E">⚡ ${text.zone.positiveLightning}</h4>
        <div class="stat-row"><span class="stat-lbl">${text.zone.impactCount}</span><span class="stat-val red">${stats.posCount}</span></div>
        <div class="stat-row"><span class="stat-lbl">${text.zone.maximumIntensity}</span><span class="stat-val red">${stats.posMaxKa.fmt(0)} kA</span></div>
        <div class="stat-row"><span class="stat-lbl">${text.zone.averageIntensity}</span><span class="stat-val red">${stats.posAvgKa.fmt(0)} kA</span></div>
        <div class="stat-row"><span class="stat-lbl">${text.zone.median}</span><span class="stat-val red">${stats.posMedianKa.fmt(0)} kA</span></div>
      </div>
    </div>"""
    }

    // ─── SVG Chart generators ───────────────────────────────────────────────

    private fun svgBarChart(
        text: KeraunicReportText,
        data: List<Pair<String, List<Pair<Int, String>>>>,
        width: Int = 580,
        height: Int = 140,
    ): String {
        // data = list of (label, [(value, color)])
        if (data.isEmpty()) return """<div style="text-align:center;padding:30px;color:#8A9AB5;font-style:italic">${text.charts.notAvailable}</div>"""

        val maxVal = data.flatMap { it.second.map { p -> p.first } }.maxOrNull() ?: 1
        val barW = 19.5
        val chartH = height - 30
        val startX = 48.0
        val step = (width - startX) / data.size

        return buildString {
            append("""<svg viewBox="0 0 $width $height" width="100%" height="$height">""")
            data.forEachIndexed { i, (label, bars) ->
                val cx = startX + i * step + step / 2
                var xOff = -(bars.size * barW / 2) + barW * 0.1
                bars.forEach { (value, color) ->
                    if (value > 0) {
                        val barH = max(2, (value.toDouble() / maxVal * chartH).roundToInt())
                        val y = chartH - barH + 10
                        append("""<rect x="${(cx + xOff).fmt(1)}" y="$y" width="$barW" height="$barH" fill="$color" rx="2"/>""")
                        if (value > maxVal * 0.1) {
                            append("""<text x="${(cx + xOff + barW / 2).fmt(1)}" y="${y - 2}" text-anchor="middle" font-size="7" fill="$color">$value</text>""")
                        }
                    }
                    xOff += barW + 1.5
                }
                append("""<text x="${cx.fmt(1)}" y="${height - 4}" text-anchor="middle" font-size="8" fill="#6B7A99">$label</text>""")
            }
            append("""<line x1="48" y1="10" x2="48" y2="${chartH + 10}" stroke="#ddd" stroke-width="1"/>""")
            append("""<line x1="48" y1="${chartH + 10}" x2="$width" y2="${chartH + 10}" stroke="#ddd" stroke-width="1"/>""")
            append("</svg>")
        }
    }

    private fun svgLineChart(
        text: KeraunicReportText,
        data: List<Pair<String, List<Pair<Double, String>>>>,
        width: Int = 580,
        height: Int = 140,
    ): String {
        if (data.isEmpty()) return """<div style="text-align:center;padding:30px;color:#8A9AB5;font-style:italic">${text.charts.notAvailable}</div>"""

        val maxVal = data.flatMap { it.second.map { p -> p.first } }.maxOrNull()?.let { if (it == 0.0) 1.0 else it } ?: 1.0
        val chartH = height - 30
        val startX = 48.0
        val step = (width - startX) / data.size

        // Group by color for line paths
        val colorPoints = mutableMapOf<String, MutableList<Pair<Double, Double>>>()
        data.forEachIndexed { i, (_, vals) ->
            val cx = startX + i * step + step / 2
            vals.forEach { (value, color) ->
                val y = chartH - (value / maxVal * chartH) + 10
                colorPoints.getOrPut(color) { mutableListOf() }.add(cx to y)
            }
        }

        return buildString {
            append("""<svg viewBox="0 0 $width $height" width="100%" height="$height">""")
            colorPoints.forEach { (color, points) ->
                if (points.size > 1) {
                    val path = points.joinToString(" L ") { "${it.first.fmt(1)},${it.second.fmt(1)}" }
                    append("""<polyline points="${points.joinToString(" ") { "${it.first.fmt(1)},${it.second.fmt(1)}" }}" fill="none" stroke="$color" stroke-width="2.5"/>""")
                }
                points.forEach { (x, y) ->
                    append("""<circle cx="${x.fmt(1)}" cy="${y.fmt(1)}" r="3" fill="$color"/>""")
                }
            }
            data.forEachIndexed { i, (label, _) ->
                val cx = startX + i * step + step / 2
                append("""<text x="${cx.fmt(1)}" y="${height - 4}" text-anchor="middle" font-size="8" fill="#6B7A99">$label</text>""")
            }
            append("""<line x1="48" y1="10" x2="48" y2="${chartH + 10}" stroke="#ddd" stroke-width="1"/>""")
            append("""<line x1="48" y1="${chartH + 10}" x2="$width" y2="${chartH + 10}" stroke="#ddd" stroke-width="1"/>""")
            append("</svg>")
        }
    }


    // ═══════════════════════════════════════════════════════════════════════
    // PAGE 1 — COVER
    // ═══════════════════════════════════════════════════════════════════════

    @Suppress("LongParameterList")
    private fun pageCover(
        siteName: String,
        customTitle: String,
        coordStr: String,
        period: String,
        nsg: Double,
        totalImpacts: Int,
        directImpacts: Int,
        maxKa: Double,
        highlights: KeraunicHighlights?,
        genDate: String,
        totalPages: Int,
        text: KeraunicReportText,
    ): String {
        val nsgLevel = when (KeraunicReportLogic.nsgLevel(nsg)) {
            KeraunicNsgLevel.HIGH -> text.cover.highActivity
            KeraunicNsgLevel.MODERATE -> text.cover.moderateActivity
            KeraunicNsgLevel.LOW -> text.cover.lowActivity
        }

        return """
<div class="page">
  <div style="padding:60px 40px 0;text-align:center;">
    <img src="data:image/png;base64,$LOGO_B64" alt="Strike Radar" style="height:72px;margin-bottom:24px;">
    <h1 style="font-size:28px;font-weight:800;color:#1A2B4A;margin-bottom:6px;">${text.reportTitle}</h1>
    <p style="font-size:16px;color:#6B7A99;margin-bottom:32px;">${text.cover.subtitle}</p>
    <div style="background:#F5F7FA;border-radius:12px;padding:24px 32px;margin-bottom:28px;text-align:left;">
      ${if (customTitle.isNotBlank()) "<div style=\"font-size:15px;font-weight:600;color:#2B7FE0;margin-bottom:6px;\">${escapeHtml(customTitle.trim())}</div>" else ""}
      <div style="font-size:22px;font-weight:700;color:#1A2B4A;margin-bottom:8px;">$siteName</div>
      <div style="font-size:14px;color:#6B7A99;margin-bottom:4px;">$coordStr</div>
      <div style="font-size:14px;color:#6B7A99;">${text.cover.period}${text.labelColon()} $period</div>
    </div>
  </div>
  <div class="kpi-grid" style="margin:0 40px;">
    <div class="kpi-card">
      <div class="kpi-value">$totalImpacts</div>
      <div class="kpi-label">${text.cover.impactsDetected}</div>
    </div>
    <div class="kpi-card">
      <div class="kpi-value blue">${nsg.fmt(2)}</div>
      <div class="kpi-label">${text.cover.nsg}</div>
    </div>
    <div class="kpi-card">
      <div class="kpi-value red">${maxKa.fmt(0)} kA</div>
      <div class="kpi-label">${text.cover.maximumIntensity}</div>
    </div>
    <div class="kpi-card">
      <div class="kpi-value">$directImpacts</div>
      <div class="kpi-label">${text.cover.directDanger}</div>
    </div>
  </div>
  ${highlightsBlock(highlights, text)}
  <div style="text-align:center;margin:28px 40px 0;padding:16px;background:#EEF4FF;border-radius:10px;">
    <div style="font-size:16px;font-weight:700;color:#2B7FE0;margin-bottom:4px;">${fill(text.cover.nsgLine, nsg.fmt(2))}</div>
    <div style="font-size:14px;color:#4A5A7A;">$nsgLevel ${text.cover.referencePrefix}</div>
  </div>
  ${footer(text, siteName, genDate, 1, totalPages)}
</div>
"""
    }

    // ═══════════════════════════════════════════════════════════════════════
    // PAGE 2 — OVERVIEW (top impacts + Nsg comparison)
    // ═══════════════════════════════════════════════════════════════════════

    @Suppress("LongParameterList")
    private fun pageOverview(
        siteName: String,
        period: String,
        topImpacts: List<KeraunicTopImpact>,
        fallbackImpacts: List<KeraunicTopImpact>,
        nsg: Double,
        nsgRefs: List<KeraunicNsgRef>,
        coordStr: String,
        genDate: String,
        totalPages: Int,
        text: KeraunicReportText,
    ): String {
        // Without top impacts, show the nearest, strongest and weakest impacts (no timestamp available)
        val useFallback = topImpacts.isEmpty() && fallbackImpacts.isNotEmpty()
        val impactsHtml = if (useFallback) {
            fallbackImpacts.joinToString("") { imp ->
                val colorClass = if (imp.isPositive) "red" else "blue"
                val polarity = if (imp.isPositive) "CG+" else "CG\u2212"
                """
      <div class="top-impact">
        <div class="top-info">
          <div class="top-ka $colorClass">${imp.amplitudeKa.fmt(1)} kA $polarity</div>
          <div class="top-meta">${text.fallbackImpactLabel(imp.externalId)} · ${imp.distKm.fmt(2)} km</div>
        </div>
      </div>"""
            }
        } else if (topImpacts.isEmpty()) {
            """<div style="padding:20px;text-align:center;color:#8A9AB5;font-style:italic">${text.overview.missingTopImpacts}</div>"""
        } else {
            topImpacts.take(5).mapIndexed { i, imp ->
                val colorClass = if (imp.isPositive) "red" else "blue"
                val polarity = if (imp.isPositive) "CG+" else "CG\u2212"
                val pctHtml = if (KeraunicReportLogic.hasPercent(imp)) {
                    """<div class="top-pct">${imp.percent.fmt(0)} %</div>"""
                } else {
                    ""
                }
                """
      <div class="top-impact">
        <div class="top-rank">${i + 1}</div>
        <div class="top-info">
          <div class="top-ka $colorClass">${imp.amplitudeKa.fmt(0)} kA $polarity</div>
          <div class="top-meta">${text.fallbackImpactLabel(imp.externalId)} · ${text.impactDate(imp)} · ${imp.distKm.fmt(2)} km</div>
        </div>
        $pctHtml
      </div>"""
            }.joinToString("")
        }

        val overviewSubtitle = if (useFallback) {
            text.overview.fallbackSubtitle
        } else {
            text.overview.topSubtitle
        }

        val nsgBar = buildNsgComparison(text, nsg, nsgRefs)
        val nsgSection = if (nsgBar.isEmpty()) {
            ""
        } else {
            "<div class=\"section-title\" style=\"margin-top:8px\">" +
                "\uD83D\uDCE1 ${text.overview.nearestCities}</div>$nsgBar"
        }

        return """
<div class="page">
  ${headerBar(text, text.overview.header, siteName, period)}
  <div class="body">
    <div class="section-title">🎯 ${text.overview.notableImpacts}</div>
    <div style="font-size:14px;color:#4A5A7A;margin-bottom:10px;">$overviewSubtitle</div>
    $impactsHtml

    <div class="section-title">📊 ${text.overview.analysis}</div>
    $nsgSection

    <div class="section-title">📡 ${text.overview.detectionNetwork}</div>
    <div style="background:#EEF2F8;border-radius:10px;padding:14px 18px;font-size:14px;color:#3A4B6A;line-height:1.6;">
      ${text.overview.detectionDescription}
    </div>
  </div>
  ${footer(text, siteName, genDate, 2, totalPages)}
</div>
"""
    }

    private fun buildNsgComparison(text: KeraunicReportText, nsg: Double, refs: List<KeraunicNsgRef>): String {
        if (refs.isEmpty()) return ""
        val maxNsg = maxOf(nsg, refs.maxOf { it.value }) * 1.2
        val siteBarWidth = (nsg / maxNsg * 100).roundToInt()

        val siteBar = """
      <div style="display:flex;align-items:center;gap:8px;margin-bottom:6px;">
        <div style="width:90px;font-size:13px;font-weight:700;color:#2B7FE0;text-align:right;">${text.overview.yourSite}</div>
        <div style="flex:1;background:#eef2f8;border-radius:4px;height:20px;overflow:hidden;">
          <div style="height:100%;width:$siteBarWidth%;background:#2B7FE0;border-radius:4px;display:flex;align-items:center;justify-content:flex-end;">
            <span style="font-size:12px;color:#fff;font-weight:700;padding-right:6px;">${nsg.fmt(2)}</span>
          </div>
        </div>
      </div>"""

        val refBars = refs.joinToString("") { ref ->
            val w = (ref.value / maxNsg * 100).roundToInt()
            """
      <div style="display:flex;align-items:center;gap:8px;margin-bottom:4px;">
        <div style="width:90px;font-size:12px;color:#6B7A99;text-align:right;">${KeraunicReportLogic.localizeCity(ref.label, text.lang)}</div>
        <div style="flex:1;background:#f5f7fa;border-radius:4px;height:16px;overflow:hidden;">
          <div style="height:100%;width:$w%;background:#C8D6E5;border-radius:4px;display:flex;align-items:center;justify-content:flex-end;">
            <span style="font-size:11px;color:#4A5A7A;font-weight:600;padding-right:5px;">${ref.value.fmt(1)}</span>
          </div>
        </div>
      </div>"""
        }

        return """
    <div style="background:#F9FAFB;border:1px solid #E0E6F0;border-radius:10px;padding:14px 18px;margin-bottom:14px;">
      $siteBar
      $refBars
    </div>"""
    }


    // ═══════════════════════════════════════════════════════════════════════
    // PAGE 3 — IMPACT MAP
    // ═══════════════════════════════════════════════════════════════════════

    private fun pageImpactMap(
        siteName: String,
        period: String,
        mapSvg: String,
        less1Km: Int,
        less2Km: Int,
        genDate: String,
        pageNum: Int,
        totalPages: Int,
        text: KeraunicReportText,
    ): String {
        val minus = "\u2212"
        val dash = "\u2013"
        return """
<div class="page">
  ${headerBar(text, text.map.header, siteName, period)}
  <div class="body" style="display:flex;flex-direction:column;align-items:center;">
    <div class="section-title">${text.map.title}</div>
    <div style="font-size:14px;color:#4A5A7A;margin-bottom:8px;text-align:center;">
      ${text.map.description}<br/>
      <span style="color:#E63B2E;font-weight:700;">${"\u25cf"}</span> CG+ (${text.map.positive}) &nbsp;
      <span style="color:#2B7FE0;font-weight:700;">${"\u25cf"}</span> CG$minus (${text.map.negative})
    </div>
    $mapSvg
    <div style="display:grid;grid-template-columns:1fr 1fr;gap:14px;margin-top:16px;width:100%;">
      <div style="background:#FFF5F5;border:1.5px solid #E63B2E;border-radius:10px;padding:14px 18px;text-align:center;">
        <div style="font-size:28px;font-weight:800;color:#E63B2E;">$less1Km</div>
        <div style="font-size:13px;color:#6B7A99;">${text.map.directCount}</div>
        <div style="font-size:11px;color:#8A9AB5;">${text.map.directDanger}</div>
      </div>
      <div style="background:#FFFBF0;border:1.5px solid #F5A800;border-radius:10px;padding:14px 18px;text-align:center;">
        <div style="font-size:28px;font-weight:800;color:#F5A800;">$less2Km</div>
        <div style="font-size:13px;color:#6B7A99;">${text.map.indirectCount}</div>
        <div style="font-size:11px;color:#8A9AB5;">${text.map.indirectDanger}</div>
      </div>
    </div>
  </div>
  ${footer(text, siteName, genDate, pageNum, totalPages)}
</div>
"""
    }

    // ═══════════════════════════════════════════════════════════════════════
    // PAGES 4-5 — ZONE ANALYSIS (parameterized for 0-1km and 1-2km)
    // ═══════════════════════════════════════════════════════════════════════

    @Suppress("LongParameterList")
    private fun pageZone(
        siteName: String,
        period: String,
        zoneLabel: String, // "0–1" or "1–2"
        zoneType: String, // "direct" or "indirect"
        stats: KeraunicZoneStats,
        topImpacts: List<KeraunicTopImpact>,
        genDate: String,
        pageNum: Int,
        totalPages: Int,
        text: KeraunicReportText,
    ): String {
        val isDirect = zoneType == "direct"
        val badgeClass = if (isDirect) "red" else "amber"
        val dangerLabel = if (isDirect) text.zone.directDanger else text.zone.indirectDanger
        val titleColor = if (isDirect) "#E63B2E" else "#D48B00"
        val zoneEmoji = if (isDirect) "\u26a1" else "\ud83c\udf29"
        val subtitle = if (isDirect) {
            text.zone.directSubtitle
        } else {
            text.zone.indirectSubtitle
        }
        val zoneDesc = if (isDirect) {
            text.zone.directDescription
        } else {
            text.zone.indirectDescription
        }
        val norm = if (isDirect) "IEC 62305-2:2024" else "IEC 62305-2:2024"

        // Polarity bar and statistics only when the breakdown is known; never show placeholders
        val hasImpacts = stats.totalCount > 0
        val hasPolarity = KeraunicReportLogic.hasPolarity(stats)
        val polarHtml = if (hasPolarity) polarityBar(text, stats.negCount, stats.posCount, stats.totalCount) else ""
        val statsHtml = when {
            !hasImpacts -> """
    <div style="text-align:center;margin:14px 0;font-size:14px;color:#8A9AB5;font-style:italic;">
      ${text.zone.noImpactsInZone}
    </div>"""
            hasPolarity -> statsGrid(text, stats)
            else -> ""
        }

        // Top impacts key points (only for direct zone with available data)
        // Only strikes really located under 1 km can be listed as key points of the direct zone
        val directTop = KeraunicReportLogic.directImpacts(topImpacts)
        val keyPointsHtml = if (isDirect && directTop.isNotEmpty()) {
            val maxImpact = directTop.maxByOrNull { it.amplitudeKa } ?: directTop.first()
            val maxPos = directTop.filter { it.isPositive }.maxByOrNull { it.amplitudeKa }
            """
    <div style="background:#FFF3E0;border:1.5px solid #F5A800;border-radius:10px;padding:14px 18px;margin:16px 0 12px;">
      <div style="font-size:15px;font-weight:700;color:#E65100;text-transform:uppercase;letter-spacing:.5px;margin-bottom:10px;">📌 ${text.zone.keyPoints}</div>
      <div style="display:grid;grid-template-columns:1fr 1fr;gap:10px;">
        <div style="background:#fff;border-radius:8px;padding:10px 14px;">
          <div style="font-size:14px;font-weight:700;color:#1A2B4A;margin-bottom:6px;">${text.zone.mostIntenseImpact}</div>
          <div style="font-size:20px;font-weight:700;color:#2B7FE0;">${maxImpact.amplitudeKa.fmt(0)} kA <span style="font-size:14px;font-weight:400;color:#6B7A99">${if (maxImpact.isPositive) "CG+" else "CG−"}</span></div>
          <div style="font-size:14px;color:#6B7A99;margin-top:4px;">${text.fallbackImpactLabel(maxImpact.externalId)} · ${text.impactDate(maxImpact)}</div>
          <div style="font-size:14px;color:#6B7A99;">${fill(text.zone.distanceFromSite, maxImpact.distKm.fmt(2))}</div>
        </div>
        ${if (maxPos != null) """
        <div style="background:#fff;border-radius:8px;padding:10px 14px;">
          <div style="font-size:14px;font-weight:700;color:#1A2B4A;margin-bottom:6px;">${text.zone.strongestPositiveImpact}</div>
          <div style="font-size:20px;font-weight:700;color:#E63B2E;">${maxPos.amplitudeKa.fmt(0)} kA <span style="font-size:14px;font-weight:400;color:#6B7A99">CG+</span></div>
          <div style="font-size:14px;color:#6B7A99;margin-top:4px;">${text.fallbackImpactLabel(maxPos.externalId)} · ${text.impactDate(maxPos)}</div>
          <div style="font-size:14px;color:#6B7A99;">${text.zone.moreDestructive}</div>
        </div>""" else ""}
      </div>
    </div>"""
        } else ""

        return """
<div class="page">
  ${headerBar(text, subtitle, siteName, period)}
  <div class="body">
    <div class="section-title">$zoneEmoji ${text.charts.zone} $zoneLabel km &nbsp;— ${if (isDirect) text.zone.zoneDirect else text.zone.zoneIndirect} ($norm)</div>
    <div class="zone-card $zoneType">
      <div class="zone-header">
        <div>
          <div class="zone-badge $badgeClass">⚠ $dangerLabel</div>
          <div class="zone-title" style="color:$titleColor">${stats.totalCount} ${text.zone.recordedImpacts}</div>
          <div class="zone-sub">$zoneDesc</div>
        </div>
      </div>
      $polarHtml
      $statsHtml
    </div>
    $keyPointsHtml
  </div>
  ${footer(text, siteName, genDate, pageNum, totalPages)}
</div>
"""
    }

    // ═══════════════════════════════════════════════════════════════════════
    // PAGE 5 — EFFECTS & CONTEXT
    // ═══════════════════════════════════════════════════════════════════════

    @Suppress("LongParameterList")
    private fun pageEffects(
        siteName: String,
        period: String,
        zone1: KeraunicZoneStats,
        zone2: KeraunicZoneStats,
        maxKa: Double,
        nsg: Double,
        genDate: String,
        totalPages: Int,
        text: KeraunicReportText,
    ): String {
        // Same risk assessment as the recommendations page, so both pages always agree
        val risk = KeraunicReportLogic.assessRisk(zone1.totalCount, maxKa, nsg)
        val riskLabel = riskLabel(text, risk.level)
        val riskNote = if (risk.level == KeraunicRiskLevel.LOW) {
            text.effects.synthesisLowNote
        } else {
            text.effects.synthesisRiskNote
        }

        return """
<div class="page">
  ${headerBar(text, text.effects.header, siteName, period)}
  <div class="body">
    <div class="section-title">${text.effects.section}</div>

    <div style="margin:0 0 16px 0;">
      <div style="font-size:16px;font-weight:700;color:#1A2B4A;margin-bottom:10px;display:flex;align-items:center;gap:8px;">
        <span style="background:#E63B2E;color:#fff;border-radius:50%;width:26px;height:26px;display:flex;align-items:center;justify-content:center;font-size:15px;flex-shrink:0;">⚡</span>
        ${text.effects.directTitle}
      </div>
      <div style="display:flex;gap:14px;">
        <div style="flex:1;background:#FFF3E0;border:1.5px solid #F5A800;border-radius:10px;padding:14px 16px;">
          <div style="font-size:15px;font-weight:700;color:#E65100;text-transform:uppercase;letter-spacing:.5px;margin-bottom:8px;">🔥 ${text.effects.fire}</div>
          <div style="font-size:14px;color:#1A2B4A;line-height:1.55;">${text.effects.fireDescription}</div>
        </div>
        <div style="flex:1;background:#FFF3E0;border:1.5px solid #F5A800;border-radius:10px;padding:14px 16px;">
          <div style="font-size:15px;font-weight:700;color:#E65100;text-transform:uppercase;letter-spacing:.5px;margin-bottom:8px;">💥 ${text.effects.structuralDamage}</div>
          <div style="font-size:14px;color:#1A2B4A;line-height:1.55;">${text.effects.structuralDamageDescription}</div>
        </div>
        <div style="flex:1;background:#FFE0B2;border:1.5px solid #E65100;border-radius:10px;padding:14px 16px;">
          <div style="font-size:15px;font-weight:700;color:#B71C1C;text-transform:uppercase;letter-spacing:.5px;margin-bottom:8px;">⚠️ ${text.effects.lifeSafety}</div>
          <div style="font-size:14px;color:#1A2B4A;line-height:1.55;">${text.effects.lifeSafetyDescription}</div>
        </div>
      </div>
    </div>

    <div style="margin:0 0 16px 0;">
      <div style="font-size:16px;font-weight:700;color:#1A2B4A;margin-bottom:10px;display:flex;align-items:center;gap:8px;">
        <span style="background:#2B7FE0;color:#fff;border-radius:50%;width:26px;height:26px;display:flex;align-items:center;justify-content:center;font-size:15px;flex-shrink:0;">〜</span>
        ${text.effects.indirectTitle}
      </div>
      <div style="display:flex;gap:14px;">
        <div style="flex:1;background:#EEF2F8;border:1.5px solid #2B7FE0;border-radius:10px;padding:14px 16px;">
          <div style="font-size:15px;font-weight:700;color:#1A4A8A;text-transform:uppercase;letter-spacing:.5px;margin-bottom:8px;">⚡ ${text.effects.lempSurges}</div>
          <div style="font-size:14px;color:#1A2B4A;line-height:1.55;">${text.effects.lempDescription}</div>
        </div>
        <div style="flex:1;background:#EEF2F8;border:1.5px solid #2B7FE0;border-radius:10px;padding:14px 16px;">
          <div style="font-size:15px;font-weight:700;color:#1A4A8A;text-transform:uppercase;letter-spacing:.5px;margin-bottom:8px;">🤖 ${text.effects.automation}</div>
          <div style="font-size:14px;color:#1A2B4A;line-height:1.55;">${text.effects.automationDescription}</div>
        </div>
      </div>
    </div>

    <div style="background:#E8F5E9;border:1.5px solid #2E7D32;border-radius:10px;padding:13px 16px;">
      <div style="font-size:15px;font-weight:700;color:#1B5E20;margin-bottom:5px;">${text.effects.synthesis}</div>
      <div style="font-size:14px;color:#1B5E20;line-height:1.55;">
        ${fill(text.effects.synthesisText, maxKa.fmt(0), zone1.totalCount.toString(), zone2.totalCount.toString(), riskLabel)} $riskNote
      </div>
    </div>
  </div>
  ${footer(text, siteName, genDate, 6, totalPages)}
</div>
"""
    }


    // ═══════════════════════════════════════════════════════════════════════
    // PAGE 6 — MONTHLY ACTIVITY
    // ═══════════════════════════════════════════════════════════════════════

    @Suppress("LongParameterList")
    private fun pageMonthlyActivity(
        siteName: String,
        period: String,
        monthly1: List<KeraunicMonthlyData>,
        monthly2: List<KeraunicMonthlyData>,
        genDate: String,
        totalPages: Int,
        text: KeraunicReportText,
    ): String {
        val chart1 = svgBarChart(text,
            monthly1.map { text.month(it.month) to listOf(it.negCount to "#2B7FE0", it.posCount to "#E63B2E") }
        )
        val chart2 = svgBarChart(text,
            monthly2.map { text.month(it.month) to listOf(it.negCount to "#2B7FE0", it.posCount to "#E63B2E") }
        )
        val chartLegend = """<div class="chart-legend"><span><span class="legend-dot" style="background:#2B7FE0"></span>${text.charts.negativeSeries}</span><span><span class="legend-dot" style="background:#E63B2E"></span>${text.charts.positiveSeries}</span></div>"""

        return """
<div class="page">
  ${headerBar(text, text.charts.monthlyAndIntensityDistribution, siteName, period)}
  <div class="body">
    <div class="section-title">📅 ${text.charts.monthlyActivity}</div>
    <div style="font-size:14px;font-weight:600;color:#2B7FE0;margin:6px 0 4px">${text.charts.zone} 0–1 km &nbsp;<span style="font-weight:400;color:#6B7A99;font-size:14px">(${text.charts.strikesPerMonth})</span></div>
    <div class="chart-box" style="margin-top:4px;padding:10px 14px">$chartLegend$chart1</div>
    <div style="font-size:14px;font-weight:600;color:#E63B2E;margin:8px 0 4px">${text.charts.zone} 1–2 km &nbsp;<span style="font-weight:400;color:#6B7A99;font-size:14px">(${text.charts.strikesPerMonth})</span></div>
    <div class="chart-box" style="padding:10px 14px">$chartLegend$chart2</div>
  </div>
  ${footer(text, siteName, genDate, 7, totalPages)}
</div>
"""
    }

    // ═══════════════════════════════════════════════════════════════════════
    // PAGE 7 — MONTHLY INTENSITY
    // ═══════════════════════════════════════════════════════════════════════

    @Suppress("LongParameterList")
    private fun pageMonthlyIntensity(
        siteName: String,
        period: String,
        monthly1: List<KeraunicMonthlyData>,
        monthly2: List<KeraunicMonthlyData>,
        genDate: String,
        totalPages: Int,
        text: KeraunicReportText,
    ): String {
        val chart1 = svgLineChart(text,
            monthly1.map { text.month(it.month) to listOf(it.negAvgKa to "#2B7FE0", it.posAvgKa to "#E63B2E") }
        )
        val chart2 = svgLineChart(text,
            monthly2.map { text.month(it.month) to listOf(it.negAvgKa to "#2B7FE0", it.posAvgKa to "#E63B2E") }
        )
        val chartLegend = """<div class="chart-legend"><span><span class="legend-dot" style="background:#2B7FE0"></span>${text.charts.negativeSeries}</span><span><span class="legend-dot" style="background:#E63B2E"></span>${text.charts.positiveSeries}</span></div>"""

        return """
<div class="page">
  ${headerBar(text, text.charts.monthlyIntensityHeader, siteName, period)}
  <div class="body">
    <div class="section-title">📈 ${text.charts.averageMonthlyIntensity}</div>
    <div style="font-size:14px;color:#4A5A7A;margin:6px 0 12px;line-height:1.6">${text.charts.monthlyIntensityDescription}</div>
    <div style="font-size:15px;font-weight:600;color:#2B7FE0;margin:0 0 4px">${text.charts.zone} 0–1 km <span style="font-weight:400;color:#6B7A99;font-size:14px">(${text.charts.averageKaPerMonth})</span></div>
    <div class="chart-box" style="padding:10px 14px">$chartLegend$chart1</div>
    <div style="font-size:15px;font-weight:600;color:#E63B2E;margin:8px 0 4px">${text.charts.zone} 1–2 km <span style="font-weight:400;color:#6B7A99;font-size:14px">(${text.charts.averageKaPerMonth})</span></div>
    <div class="chart-box" style="padding:10px 14px">$chartLegend$chart2</div>
  </div>
  ${footer(text, siteName, genDate, 8, totalPages)}
</div>
"""
    }

    // ═══════════════════════════════════════════════════════════════════════
    // PAGE 8 — HOURLY DISTRIBUTION
    // ═══════════════════════════════════════════════════════════════════════

    @Suppress("LongParameterList")
    private fun pageHourlyDistribution(
        siteName: String,
        period: String,
        hourly1: List<KeraunicHourlyData>,
        genDate: String,
        totalPages: Int,
        text: KeraunicReportText,
    ): String {
        val chart1 = svgBarChart(text,
            hourly1.map { text.hour(it.hour) to listOf(it.negCount to "#2B7FE0", it.posCount to "#E63B2E") }
        )
        val chartLegend = """<div class="chart-legend"><span><span class="legend-dot" style="background:#2B7FE0"></span>${text.charts.negativeSeries}</span><span><span class="legend-dot" style="background:#E63B2E"></span>${text.charts.positiveSeries}</span></div>"""

        return """
<div class="page">
  ${headerBar(text, text.charts.hourlyDistributionHeader, siteName, period)}
  <div class="body">
    <div class="section-title">⏱ ${text.charts.hourlyDistribution}</div>
    <div style="font-size:14px;font-weight:600;color:#2B7FE0;margin:8px 0 4px">${text.charts.allImpactsAroundSite} <span style="font-weight:400;color:#6B7A99;font-size:14px">(${text.charts.combinedZones})</span></div>
    <div class="chart-box" style="padding:10px 14px">$chartLegend$chart1</div>
  </div>
  ${footer(text, siteName, genDate, 9, totalPages)}
</div>
"""
    }

    // ═══════════════════════════════════════════════════════════════════════
    // PAGE 9 — HOURLY INTENSITY
    // ═══════════════════════════════════════════════════════════════════════

    @Suppress("LongParameterList")
    private fun pageHourlyIntensity(
        siteName: String,
        period: String,
        hourly1: List<KeraunicHourlyData>,
        genDate: String,
        totalPages: Int,
        text: KeraunicReportText,
    ): String {
        val chart1 = svgLineChart(text,
            hourly1.map { text.hour(it.hour) to listOf(it.negAvgKa to "#2B7FE0", it.posAvgKa to "#E63B2E") }
        )
        val chartLegend = """<div class="chart-legend"><span><span class="legend-dot" style="background:#2B7FE0"></span>${text.charts.negativeSeries}</span><span><span class="legend-dot" style="background:#E63B2E"></span>${text.charts.positiveSeries}</span></div>"""

        return """
<div class="page">
  ${headerBar(text, text.charts.monthlyHourlyIntensityHeader, siteName, period)}
  <div class="body">
    <div class="section-title">📈 ${text.charts.averageHourlyIntensity}</div>
    <div style="font-size:14px;color:#4A5A7A;margin:6px 0 12px;line-height:1.6">${text.charts.dailyProfile}</div>
    <div style="font-size:15px;font-weight:600;color:#2B7FE0;margin:0 0 4px">${text.charts.allImpactsAroundSite} <span style="font-weight:400;color:#6B7A99;font-size:14px">(${text.charts.averageKaPerHour})</span></div>
    <div class="chart-box" style="padding:10px 14px">$chartLegend$chart1</div>
  </div>
  ${footer(text, siteName, genDate, 10, totalPages)}
</div>
"""
    }


    // ═══════════════════════════════════════════════════════════════════════
    // PAGE 10 — RECOMMENDATIONS
    // ═══════════════════════════════════════════════════════════════════════

    @Suppress("LongParameterList")
    private fun pageRecommendations(
        siteName: String,
        period: String,
        zone1: KeraunicZoneStats,
        zone2: KeraunicZoneStats,
        maxKa: Double,
        nsg: Double,
        questionnaire: PdfQuestionnaireData?,
        genDate: String,
        totalPages: Int,
        text: KeraunicReportText,
    ): String {
        // Risk score: 0-100 based on direct impacts, max intensity, Nsg
        val risk = KeraunicReportLogic.assessRisk(zone1.totalCount, maxKa, nsg)
        val riskScore = risk.score
        val riskLabel = riskLabel(text, risk.level)
        val riskColor = when {
            riskScore >= 75 -> "#E63B2E"
            riskScore >= 40 -> "#F5A800"
            else -> "#4CAF50"
        }

        // SVG gauge
        val needleAngle = KeraunicReportLogic.gaugeNeedleDegrees(riskScore)
        val needleRad = needleAngle * PI / 180
        val nx = 145 + 75 * cos(needleRad)
        val ny = 115 + 75 * sin(needleRad)

        val gaugeHtml = """
    <div class="cover-risk">
      <svg viewBox="0 0 290 150" width="290" height="150">
        <path d="M 55 115 A 90 90 0 0 1 100 37" stroke="#4CAF50" stroke-width="14" fill="none" stroke-linecap="round"/>
        <path d="M 100 37 A 90 90 0 0 1 190 37" stroke="#F5A800" stroke-width="14" fill="none" stroke-linecap="round"/>
        <path d="M 190 37 A 90 90 0 0 1 230 84" stroke="#E63B2E" stroke-width="14" fill="none" stroke-linecap="round"/>
        <path d="M 230 84 A 90 90 0 0 1 235 115" stroke="#8B0000" stroke-width="14" fill="none" stroke-linecap="round"/>
        <line x1="145" y1="115" x2="${nx.fmt(1)}" y2="${ny.fmt(1)}" stroke="#1A2B4A" stroke-width="3" stroke-linecap="round"/>
        <circle cx="145" cy="115" r="6" fill="#1A2B4A"/>
        <text x="145" y="87" text-anchor="middle" font-size="28" fill="$riskColor" font-weight="900">$riskScore</text>
        <text x="145" y="103" text-anchor="middle" font-size="10" fill="#6B7A99">/100</text>
        <text x="55" y="135" text-anchor="middle" font-size="10" fill="#4CAF50" font-weight="bold">${text.recommendations.gaugeLow}</text>
        <text x="235" y="135" text-anchor="middle" font-size="10" fill="#8B0000" font-weight="bold">${text.recommendations.gaugeCritical}</text>
      </svg>
      <div>
        <div class="cover-risk-label" style="color:$riskColor">$riskLabel</div>
        <div class="cover-risk-sub">
          ${fill(text.recommendations.scoreDescription, zone1.totalCount.toString(), maxKa.fmt(0), nsg.fmt(2))}
        </div>
      </div>
    </div>"""

        // Action plan shared with the screen (KeraunicReportLogic.recommendations)
        val recos = KeraunicReportLogic.recommendations(
            directCount = zone1.totalCount,
            hasRod = questionnaire?.hasLightningRod ?: false,
            hasSurge = questionnaire?.hasSurgeProtection ?: false,
        ).map { reco ->
            val (cardClass, prioClass, prioLabel) = when (reco.priority) {
                KeraunicRecommendationPriority.CRITICAL -> Triple("critical", "p1", text.recommendations.priority1)
                KeraunicRecommendationPriority.IMPORTANT -> Triple("important", "p2", text.recommendations.priority2)
                KeraunicRecommendationPriority.ADVISORY -> Triple("advisory", "p3", text.recommendations.advice)
                KeraunicRecommendationPriority.SKY -> Triple("sky", "px", text.recommendations.skySentinel)
            }
            val (title, description) = recommendationText(text, reco.kind, zone1.totalCount, zone2.totalCount, maxKa)
            """
    <div class="reco-card $cardClass">
      <div class="reco-prio $prioClass">$prioLabel</div>
      <div class="reco-body">
        <div class="reco-title">$title</div>
        <div class="reco-desc">$description</div>
      </div>
    </div>"""
        }

        return """
<div class="page">
  ${headerBar(text, text.recommendations.header, siteName, period)}
  <div class="body">
    $gaugeHtml
    <div class="section-title">🔧 ${text.recommendations.actionPlan}</div>
    ${recos.joinToString("\n")}
  </div>
  ${footer(text, siteName, genDate, 11, totalPages)}
</div>
"""
    }

    /** Title and description of an action (HTML copy; the screen applies [KeraunicReportText.plain]). */
    internal fun recommendationText(
        text: KeraunicReportText,
        kind: KeraunicRecommendationKind,
        directCount: Int,
        indirectCount: Int,
        maxKa: Double,
    ): Pair<String, String> {
        val r = text.recommendations
        return when (kind) {
            KeraunicRecommendationKind.INSTALL_ROD ->
                r.installRodTitle to fill(r.installRodDescription, directCount.toString(), maxKa.fmt(0))
            KeraunicRecommendationKind.GROUNDING -> r.groundingTitle to r.groundingDescription
            KeraunicRecommendationKind.SURGE_PROTECTION -> r.surgeTitle to fill(r.surgeDescription, indirectCount.toString())
            KeraunicRecommendationKind.RISK_ASSESSMENT -> r.riskAssessmentTitle to r.riskAssessmentDescription
            KeraunicRecommendationKind.ALERTS -> r.alertsTitle to r.alertsDescription
        }
    }

    // ═══════════════════════════════════════════════════════════════════════
    // PAGE 12 — OBSERVATIONS (when typed) + METHODOLOGY
    // ═══════════════════════════════════════════════════════════════════════

    @Suppress("LongParameterList")
    private fun pageMethodology(
        siteName: String,
        period: String,
        observations: String,
        genDate: String,
        totalPages: Int,
        text: KeraunicReportText,
    ) = """
<div class="page">
  ${headerBar(text, text.methodology.header, siteName, period)}
  <div class="body">
    ${observationsBlock(observations, text)}
    <div class="section-title" style="margin-top:18px">📖 ${text.methodology.section}</div>
    <div style="margin-top:14px;background:#EEF2F8;border-radius:12px;padding:20px 22px;font-size:14px;color:#3A4B6A;line-height:2.0">
      <p style="margin:0 0 12px"><strong>${text.methodology.networkLabel}</strong> ${text.methodology.networkText}</p>
      <p style="margin:0 0 12px"><strong>${text.methodology.referencesLabel}</strong></p>
      <ul style="margin:0 0 12px;padding-left:20px;line-height:2.0">
        <li><strong>IEC 62305-2:2024</strong> — ${text.methodology.iec62305}</li>
        <li><strong>IEC 62858:2019</strong> — ${text.methodology.iec62858}</li>
        <li><strong>NF C 17-102:2011</strong> — ${text.methodology.nfc17102}</li>
      </ul>
      <p style="margin:0 0 12px"><strong>${text.methodology.coverageLabel}</strong> ${text.methodology.coverageText}</p>
      <p style="margin:0 0 12px"><strong>${text.methodology.polarityLabel}</strong></p>
      <ul style="margin:0 0 12px;padding-left:20px;line-height:2.0">
        <li><span style="color:#2B7FE0;font-weight:600">${text.methodology.negativePolarity}</span></li>
        <li><span style="color:#E63B2E;font-weight:600">${text.methodology.positivePolarity}</span></li>
      </ul>
      <p style="margin:0 0 12px"><strong>${text.methodology.unitsLabel}</strong> ${text.methodology.unitsText}</p>
      <p style="margin:0 0 12px"><strong>${text.methodology.zonesLabel}</strong> ${text.methodology.zonesText}</p>
    </div>
  </div>
  ${footer(text, siteName, genDate, 12, totalPages)}
</div>
"""

    private fun observationsBlock(observations: String, text: KeraunicReportText): String {
        if (observations.isBlank()) return ""
        return """
    <div class="section-title" style="margin-top:18px">📝 ${text.methodology.observations}</div>
    <div style="margin-top:10px;background:#FFFFFF;border:1px solid #E0E6F0;border-radius:12px;padding:16px 20px;font-size:14px;color:#1A2B4A;line-height:1.6;white-space:pre-wrap;">${escapeHtml(observations.trim())}</div>"""
    }

    // Key facts of the cover (same lines as the screen)
    private fun highlightsBlock(highlights: KeraunicHighlights?, text: KeraunicReportText): String {
        val lines = listOfNotNull(
            highlights?.strongest?.let { text.highlights.strongestImpact to text.highlightLine(it, distanceFirst = false) },
            highlights?.nearest?.let { text.highlights.nearestImpact to text.highlightLine(it, distanceFirst = true) },
        )
        val rows = if (lines.isEmpty()) {
            if (highlights?.noGroundImpact != true) return ""
            """<div style="font-size:14px;color:#1A2B4A;line-height:1.7;">${text.highlights.noGroundImpact}</div>"""
        } else {
            lines.joinToString("\n") { (label, value) ->
                """<div style="font-size:14px;color:#1A2B4A;line-height:1.7;"><strong>$label${text.labelColon()}</strong> $value</div>"""
            }
        }
        return """
  <div style="margin:18px 40px 0;padding:12px 18px;background:#FFF8E8;border:1.5px solid #F5A800;border-radius:10px;">
    <div style="font-size:13px;font-weight:700;color:#D48B00;text-transform:uppercase;letter-spacing:.5px;margin-bottom:4px;">⚡ ${text.highlights.title}</div>
    $rows
  </div>"""
    }
}
